import React, { memo } from 'react';
import PropTypes from 'prop-types';
import {
  SearchFiltersActions,
  SearchFiltersMobileDialog,
  SearchFiltersMobileTrigger,
  SearchFiltersSidebar,
  Stack
} from 'investira.react.components';

/**
 * Compõe os shells responsivos da família SearchFilters sem conhecer o domínio consumidor.
 *
 * @param {Object} pProps Propriedades do wrapper.
 * @param {'desktop'|'mobile'} pProps.displayMode Apresentação escolhida pelo template.
 * @param {string} pProps.id Identificador estável do diálogo mobile.
 * @param {string} pProps.title Título dos filtros.
 * @param {number} pProps.appliedCount Quantidade de filtros efetivamente aplicados.
 * @param {boolean} pProps.open Indica se o diálogo mobile está aberto.
 * @param {boolean} pProps.applyDisabled Impede a aplicação de valores inválidos.
 * @param {Function} pProps.onApply Aplica o rascunho atual.
 * @param {Function} pProps.onClear Limpa o rascunho atual.
 * @param {Function} pProps.onOpen Abre os filtros mobile.
 * @param {Function} pProps.onClose Fecha os filtros mobile e descarta o rascunho.
 * @param {React.ReactNode} pProps.children Conteúdo controlado dos filtros.
 * @returns {JSX.Element} Sidebar desktop ou gatilho e diálogo mobile.
 */
const SearchFiltersWrapper = memo(pProps => {
  const {
    displayMode,
    id,
    title,
    appliedCount = 0,
    open = false,
    applyDisabled = false,
    onApply,
    onClear,
    onOpen,
    onClose,
    children
  } = pProps;
  const xActions = (
    <SearchFiltersActions applyDisabled={applyDisabled} onApply={onApply} onClear={onClear} />
  );

  if (displayMode === 'mobile') {
    return (
      <Stack alignItems="flex-end">
        <SearchFiltersMobileTrigger appliedCount={appliedCount} label={title} onClick={onOpen} />
        <SearchFiltersMobileDialog
          id={id}
          title={title}
          open={open}
          onClose={onClose}
          actions={xActions}>
          {children}
        </SearchFiltersMobileDialog>
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        color: 'text.primary',
        '& [data-search-filters-sidebar-header]': {
          pt: 2
        }
      }}>
      <SearchFiltersSidebar title={title} actions={xActions}>
        {children}
      </SearchFiltersSidebar>
    </Stack>
  );
});

SearchFiltersWrapper.propTypes = {
  displayMode: PropTypes.oneOf(['desktop', 'mobile']).isRequired,
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  appliedCount: PropTypes.number,
  open: PropTypes.bool,
  applyDisabled: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired
};

SearchFiltersWrapper.displayName = 'SearchFiltersWrapper';

export default SearchFiltersWrapper;
